/**
 * GA4 ecommerce via dataLayer (GTM).
 * Events: view_item_list, view_item, add_to_cart, remove_from_cart,
 *         view_cart, begin_checkout, purchase
 */
(() => {
  const cfg = () => window.LW_DATALAYER || {};

  const currencyFallback = () =>
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
    cfg().currency ||
    'EUR';

  const cartJsUrl = () => cfg().cartJsUrl || '/cart.js';

  const toMoney = (cents) => Number((Number(cents || 0) / 100).toFixed(2));

  const asNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const n = Number(String(value ?? '').replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const normalizeItem = (item) => {
    if (!item) return null;
    return {
      item_id: String(item.item_id || ''),
      item_name: String(item.item_name || ''),
      item_brand: String(item.item_brand || 'LiveWire'),
      item_variant: String(item.item_variant || ''),
      item_category: String(item.item_category || ''),
      item_list_id: item.item_list_id != null ? String(item.item_list_id) : undefined,
      item_list_name: item.item_list_name != null ? String(item.item_list_name) : undefined,
      index: item.index != null ? Number(item.index) : undefined,
      price: asNumber(item.price),
      quantity: Math.max(1, asNumber(item.quantity) || 1),
    };
  };

  const normalizeItems = (items) =>
    (Array.isArray(items) ? items : []).map(normalizeItem).filter((item) => item && item.item_id);

  const pushEvent = (event, ecommerce) => {
    if (!event) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event,
      ecommerce: ecommerce || {},
    });
  };

  const itemFromLineItem = (line, quantityOverride) => {
    if (!line) return null;
    const quantity = quantityOverride != null ? Number(quantityOverride) : Number(line.quantity || 1);
    if (!Number.isFinite(quantity) || quantity <= 0) return null;
    return normalizeItem({
      item_id: line.sku || String(line.variant_id || line.id || ''),
      item_name: line.product_title || line.title || '',
      item_brand: line.vendor || 'LiveWire',
      item_variant: line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : '',
      item_category: line.product_type || '',
      price: toMoney(line.final_price != null ? line.final_price : line.price),
      quantity,
    });
  };

  const itemsFromCart = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.map((line) => itemFromLineItem(line)).filter(Boolean);
  };

  const cartValue = (cart) => toMoney(cart && cart.total_price);

  const cartCoupon = (cart) => {
    const codes = (cart && cart.discount_codes) || [];
    return codes
      .filter((d) => d && d.applicable && d.code)
      .map((d) => d.code)
      .join(',');
  };

  const viewItemList = ({ item_list_id, item_list_name, currency, items }) => {
    const normalized = normalizeItems(items);
    if (!normalized.length) return;
    pushEvent('view_item_list', {
      item_list_id: item_list_id || '',
      item_list_name: item_list_name || '',
      currency: currency || currencyFallback(),
      items: normalized,
    });
  };

  const viewItem = ({ currency, value, items }) => {
    const normalized = normalizeItems(items);
    if (!normalized.length) return;
    pushEvent('view_item', {
      currency: currency || currencyFallback(),
      value: asNumber(value),
      items: normalized,
    });
  };

  const addToCart = ({ currency, value, items }) => {
    const normalized = normalizeItems(items);
    if (!normalized.length) return;
    pushEvent('add_to_cart', {
      currency: currency || currencyFallback(),
      value: asNumber(value),
      items: normalized,
    });
  };

  const removeFromCart = ({ currency, value, items }) => {
    const normalized = normalizeItems(items);
    if (!normalized.length) return;
    pushEvent('remove_from_cart', {
      currency: currency || currencyFallback(),
      value: asNumber(value),
      items: normalized,
    });
  };

  const viewCart = ({ currency, value, items }) => {
    const normalized = normalizeItems(items);
    pushEvent('view_cart', {
      currency: currency || currencyFallback(),
      value: asNumber(value),
      items: normalized,
    });
  };

  const beginCheckout = ({ currency, value, items, coupon }) => {
    const normalized = normalizeItems(items);
    if (!normalized.length) return;
    const payload = {
      currency: currency || currencyFallback(),
      value: asNumber(value),
      items: normalized,
    };
    if (coupon) payload.coupon = coupon;
    pushEvent('begin_checkout', payload);
  };

  const purchase = ({ currency, value, items, transaction_id, tax, shipping, coupon }) => {
    const normalized = normalizeItems(items);
    if (!transaction_id || !normalized.length) return;
    const payload = {
      currency: currency || currencyFallback(),
      value: asNumber(value),
      transaction_id: String(transaction_id),
      items: normalized,
    };
    if (tax != null) payload.tax = asNumber(tax);
    if (shipping != null) payload.shipping = asNumber(shipping);
    if (coupon) payload.coupon = coupon;
    pushEvent('purchase', payload);
  };

  let lastAddKey = '';
  let lastAddAt = 0;

  const addToCartFromLineItem = (line, currency) => {
    const item = itemFromLineItem(line);
    if (!item) return;
    const key = String(line.variant_id || line.id || '') + ':' + String(line.quantity || 1);
    const now = Date.now();
    if (key === lastAddKey && now - lastAddAt < 2000) return;
    lastAddKey = key;
    lastAddAt = now;
    addToCart({
      currency: currency || currencyFallback(),
      value: Number((item.price * item.quantity).toFixed(2)),
      items: [item],
    });
  };

  const removeFromCartDelta = (line, qtyRemoved, currency) => {
    const item = itemFromLineItem(line, qtyRemoved);
    if (!item) return;
    removeFromCart({
      currency: currency || currencyFallback(),
      value: Number((item.price * item.quantity).toFixed(2)),
      items: [item],
    });
  };

  const addToCartDelta = (line, qtyAdded, currency) => {
    const item = itemFromLineItem(line, qtyAdded);
    if (!item) return;
    addToCart({
      currency: currency || currencyFallback(),
      value: Number((item.price * item.quantity).toFixed(2)),
      items: [item],
    });
  };

  const fetchCart = async () => {
    try {
      const res = await fetch(cartJsUrl(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (_) {
      return null;
    }
  };

  const viewCartFromCart = async () => {
    const cart = await fetchCart();
    if (!cart) return;
    viewCart({
      currency: cart.currency || currencyFallback(),
      value: cartValue(cart),
      items: itemsFromCart(cart),
    });
  };

  const beginCheckoutFromCart = async () => {
    const cart = await fetchCart();
    if (!cart || !cart.items || !cart.items.length) return false;
    beginCheckout({
      currency: cart.currency || currencyFallback(),
      value: cartValue(cart),
      items: itemsFromCart(cart),
      coupon: cartCoupon(cart) || undefined,
    });
    return true;
  };

  const isCheckoutPath = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      return /\/checkout\/?$/.test(url.pathname) || url.pathname.includes('/checkouts/');
    } catch (_) {
      return false;
    }
  };

  const isCheckoutControl = (el) => {
    if (!el || !(el instanceof Element)) return false;
    if (el.matches('[data-lw-checkout], [name="checkout"], #checkout')) return true;
    if (el.matches('a[href]') && isCheckoutPath(el.getAttribute('href') || '')) return true;
    return false;
  };

  const ensureCheckoutHiddenField = (form) => {
    if (form.querySelector('input[type="hidden"][name="checkout"]')) return;
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'checkout';
    hidden.value = '';
    form.appendChild(hidden);
  };

  const bindCheckoutTracking = () => {
    document.addEventListener(
      'click',
      async (event) => {
        const target = event.target instanceof Element ? event.target.closest('a, button, input') : null;
        if (!isCheckoutControl(target)) return;
        if (target.dataset.lwDlCheckoutSent === '1') return;
        if (target.matches('button[name="checkout"], input[name="checkout"], [data-lw-checkout]')) return;

        if (target.matches('a[href]') && isCheckoutPath(target.getAttribute('href') || '')) {
          event.preventDefault();
          target.dataset.lwDlCheckoutSent = '1';
          try {
            await beginCheckoutFromCart();
          } finally {
            window.location.href = target.href;
          }
        }
      },
      true
    );

    document.addEventListener(
      'submit',
      async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.dataset.lwDlCheckoutSent === '1') return;
        const checkoutBtn = form.querySelector('[name="checkout"], [data-lw-checkout]');
        if (!checkoutBtn) return;

        event.preventDefault();
        form.dataset.lwDlCheckoutSent = '1';
        try {
          await beginCheckoutFromCart();
        } finally {
          // Native form.submit() omits the clicked submitter. Shopify needs
          // name="checkout" in the POST body to redirect to checkout (not /cart).
          ensureCheckoutHiddenField(form);
          HTMLFormElement.prototype.submit.call(form);
        }
      },
      true
    );
  };

  const readJsonScript = (id) => {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      return { el, data: JSON.parse(el.textContent) };
    } catch (e) {
      console.warn('[lw-datalayer] invalid JSON in #' + id, e);
      return null;
    }
  };

  const pushViewItemFromDom = () => {
    const parsed = readJsonScript('lw-datalayer-product-json');
    if (!parsed || parsed.el.dataset.lwDlViewSent === '1') return;
    parsed.el.dataset.lwDlViewSent = '1';
    viewItem({
      currency: parsed.data.currency,
      value: parsed.data.value,
      items: parsed.data.items || [],
    });
    window.LW_DATALAYER = window.LW_DATALAYER || {};
    window.LW_DATALAYER.product = parsed.data;
  };

  const pushViewItemListFromDom = () => {
    const parsed = readJsonScript('lw-datalayer-item-list-json');
    if (!parsed || parsed.el.dataset.lwDlListSent === '1') return;
    if (!parsed.data.items || !parsed.data.items.length) return;
    parsed.el.dataset.lwDlListSent = '1';
    viewItemList(parsed.data);
  };

  const pushViewItemListFromHtml = (html, { item_list_id, item_list_name, currency } = {}) => {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.getElementById('lw-datalayer-item-list-json');
      if (!el) return;
      const data = JSON.parse(el.textContent);
      viewItemList({
        item_list_id: item_list_id || data.item_list_id,
        item_list_name: item_list_name || data.item_list_name,
        currency: currency || data.currency,
        items: data.items || [],
      });
    } catch (e) {
      console.warn('[lw-datalayer] view_item_list from HTML failed', e);
    }
  };

  const pushViewCartFromDom = () => {
    const parsed = readJsonScript('lw-datalayer-cart-json');
    if (!parsed || parsed.el.dataset.lwDlCartSent === '1') return;
    parsed.el.dataset.lwDlCartSent = '1';
    viewCart({
      currency: parsed.data.currency,
      value: parsed.data.value,
      items: parsed.data.items || [],
    });
  };

  const pushPurchaseFromDom = () => {
    const parsed = readJsonScript('lw-datalayer-purchase-json');
    if (!parsed || parsed.el.dataset.lwDlPurchaseSent === '1') return;
    const orderId = parsed.data.order_id || parsed.data.transaction_id;
    if (!orderId) return;
    const key = 'lw_dl_purchase_' + orderId;
    try {
      if (sessionStorage.getItem(key)) {
        parsed.el.dataset.lwDlPurchaseSent = '1';
        return;
      }
      sessionStorage.setItem(key, '1');
    } catch (_) {
      /* private mode — still fire once per element */
    }
    parsed.el.dataset.lwDlPurchaseSent = '1';
    purchase(parsed.data);
  };

  window.LivewireDataLayer = {
    pushEvent,
    viewItemList,
    viewItem,
    addToCart,
    removeFromCart,
    viewCart,
    beginCheckout,
    purchase,
    addToCartFromLineItem,
    removeFromCartDelta,
    addToCartDelta,
    beginCheckoutFromCart,
    viewCartFromCart,
    pushViewItemListFromHtml,
    fetchCart,
    itemFromLineItem,
    itemsFromCart,
    toMoney,
  };

  bindCheckoutTracking();

  const boot = () => {
    pushViewItemFromDom();
    pushViewItemListFromDom();
    pushViewCartFromDom();
    pushPurchaseFromDom();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
